import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export interface Package {
  id: string
  name: string
  slug: string
  description: string | null
  price_monthly: number
  price_yearly: number
  is_free: boolean
  is_active: boolean
  is_default: boolean
  sort_order: number
  notifications_per_month: number
  live_sessions_per_month: number
  api_calls_per_month: number
  max_applications: number
  max_team_members: number
  max_api_keys: number
  features: string[]
  stripe_price_id_monthly: string | null
  stripe_price_id_yearly: string | null
  created_at: string
  updated_at: string
}

export interface CreatePackageData {
  name: string
  slug: string
  description?: string
  price_monthly: number
  price_yearly: number
  is_free?: boolean
  is_active?: boolean
  is_default?: boolean
  sort_order?: number
  notifications_per_month: number
  live_sessions_per_month: number
  api_calls_per_month: number
  max_applications: number
  max_team_members: number
  max_api_keys: number
  features?: string[]
  stripe_price_id_monthly?: string
  stripe_price_id_yearly?: string
}

export interface UpdatePackageData {
  name?: string
  slug?: string
  description?: string
  price_monthly?: number
  price_yearly?: number
  is_free?: boolean
  is_active?: boolean
  is_default?: boolean
  sort_order?: number
  notifications_per_month?: number
  live_sessions_per_month?: number
  api_calls_per_month?: number
  max_applications?: number
  max_team_members?: number
  max_api_keys?: number
  features?: string[]
  stripe_price_id_monthly?: string
  stripe_price_id_yearly?: string
}

interface PackagesListResponse {
  packages: Package[]
  total: number
}

// Get all packages (admin)
export function useAdminPackages() {
  return useQuery({
    queryKey: ['admin', 'packages'],
    queryFn: () => api.get<PackagesListResponse>('/packages'),
  })
}

// Get a single package
export function useAdminPackage(id: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'packages', id],
    queryFn: () => api.get<Package>(`/packages/${id}`),
    enabled: !!id,
  })
}

// Create a new package
export function useCreatePackage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreatePackageData) => api.post<Package>('/packages', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'packages'] })
    },
  })
}

// Update a package
export function useUpdatePackage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePackageData }) =>
      api.patch<Package>(`/packages/${id}`, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'packages'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'packages', id] })
    },
  })
}

// Delete a package
export function useDeletePackage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/packages/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'packages'] })
    },
  })
}

// Format limit for display
export function formatLimit(value: number): string {
  if (value === -1) return 'Unlimited'
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`
  return value.toString()
}

// Format price for display (cents to dollars)
export function formatPrice(price: number): string {
  if (price === 0) return 'Free'
  return `$${(price / 100).toFixed(2)}`
}
